"use client";

import { forwardRef } from "react";
import { cn } from "@/lib/utils";

type InputSize = "default" | "compact";
type InputVariant = "filled" | "ghost";

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Size variant: "default" for user-facing forms, "compact" for admin/dense layouts */
  inputSize?: InputSize;
  /** Visual variant: "filled" (default, bordered) or "ghost" (bottom-border only, glass focus) */
  variant?: InputVariant;
  /** Show error styling (red border and focus ring) */
  error?: boolean;
  /** Icon rendered inside the input on the left side */
  leftIcon?: React.ReactNode;
  /** Icon rendered inside the input on the right side */
  rightIcon?: React.ReactNode;
  /** Accessible description linking input to an error/help message via aria-describedby */
  "aria-describedby"?: string;
  /** Accessible invalid state for screen readers */
  "aria-invalid"?: boolean;
}

const sizeStyles: Record<InputSize, string> = {
  default: "px-4 py-3 rounded-xl",
  compact: "px-3 py-2 text-sm rounded-lg",
};

// Ghost style: no fill, bottom border. Focus: glass-light fill + accent underline.
const ghostBaseStyles = "bg-transparent border-0 border-b border-b-[var(--outline-variant)]";
const ghostFocusStyles = "focus:bg-white/55 focus:backdrop-blur-sm focus:border-b-accent";

/**
 * Input - Styled text input with size variants and icon support
 *
 * Sizes:
 * - default: Standard size for user-facing forms (rounded-xl, comfortable padding)
 * - compact: Smaller size for admin/dense layouts (rounded-lg, tighter padding)
 *
 * Features:
 * - Optional left/right icons positioned inside the input
 * - Error state with red border and focus ring
 * - Disabled state with reduced opacity
 * - Full-width by default
 * - forwardRef for form library compatibility
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      inputSize = "default",
      variant = "filled",
      error = false,
      leftIcon,
      rightIcon,
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    const hasLeftIcon = !!leftIcon;
    const hasRightIcon = !!rightIcon;

    return (
      <div className="relative w-full">
        {leftIcon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-foreground-muted">
            {leftIcon}
          </div>
        )}
        <input
          ref={ref}
          disabled={disabled}
          aria-invalid={error || undefined}
          className={cn(
            // Base styles
            "w-full text-foreground placeholder:text-foreground-muted transition-all",
            // Size styles
            sizeStyles[inputSize],
            // Variant styles
            variant === "ghost"
              ? cn(ghostBaseStyles, error ? "border-b-red-500" : ghostFocusStyles)
              : cn(
                  "border bg-white",
                  error
                    ? "border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                    : "border-surface-dim focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                ),
            // Disabled state
            disabled && "opacity-50 cursor-not-allowed bg-surface-container-low",
            // Icon padding adjustments
            hasLeftIcon && "pl-10",
            hasRightIcon && "pr-10",
            className
          )}
          {...props}
        />
        {rightIcon && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-foreground-muted">
            {rightIcon}
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";
